import { Injectable } from '@angular/core';
import { AmorphicService } from '../amorphic/amorphic.service';
import { BaseQuote } from '../../../../common/js/quote/BaseQuote';
import { Utils } from '../../../../common/js/Utils';
import { PremiumRequest, PremiumRequestList } from '../../../../common/js/productService/ProductServiceClient';
import { RateClasses } from '../../../../common/js/static/RateClasses';
import { ProductQuoteResponse, ProductQuoteResponseList } from '../../../js/interfaces/CoverpathTypes';
import { Product, RiderType } from '../../../../common/js/static/Product';
import { CommonUtils } from '../../../../common/js/static/CommonUtils';

@Injectable()
export class QuoteService {
  constructor(private amorphicService: AmorphicService) {}

  public fetchProductQuote(quote: PremiumRequest): Promise<ProductQuoteResponse> {
    return this.amorphicService.controller.publicFetchProductQuote(quote);
  }

  public async fetchMultipleProductQuotes(premiumRequestList: PremiumRequestList): Promise<ProductQuoteResponseList> {
    return Promise.all(
      premiumRequestList.requests.map((request) => this.amorphicService.controller.publicFetchProductQuote(request))
    ).then((responses) => ({ responses }));
  }

  public static convertQuoteToProductServiceRequest(quote: BaseQuote, effectiveDate?: Date): PremiumRequest {
    return {
      product: quote.product,
      productType: quote.productType,
      gender: quote.gender,
      ratingCode: RateClasses[quote.healthClass].code,
      dob: quote.dateOfBirth,
      effectiveDate: effectiveDate || Utils.now(),
      state: quote.contractState,
      term: quote.term,
      face: quote.faceAmount,
      flatExtras: [],
      waiver: quote.waiverOfPremium,
      paymentFrequency: quote.paymentFrequency,
      riders: quote.riders.map((rider) => ({
        type: rider.type,
        amount: rider.amount,
      })),
      asOfDate: Utils.now(),
    };
  }

  public static convertQuoteToProductServiceRequestList(
    quote: BaseQuote,
    additionalRequests?: Array<string>,
    effectiveDate?: Date
  ): PremiumRequestList {
    const premiumRequestList: PremiumRequestList = {
      requests: [],
    };

    const regularPremiumRequest: PremiumRequest = {
      product: quote.product,
      productType: quote.productType,
      gender: quote.gender,
      ratingCode: RateClasses[quote.healthClass].code,
      dob: quote.dateOfBirth,
      effectiveDate: effectiveDate || Utils.now(),
      state: quote.contractState,
      term: quote.term,
      face: quote.faceAmount,
      flatExtras: [],
      waiver: quote.waiverOfPremium,
      paymentFrequency: quote.paymentFrequency,
      riders: quote.riders
        .map((rider) => ({
          type: rider.type,
          amount: rider.amount,
          accelerationPercent: rider.benefitDisbursementMethod,
          monthlyIncreaseOption: rider.monthlyIncreaseOption,
        }))
        .filter((rider) => {
          if (rider.type === RiderType.ALIR) {
            return false;
          }
          if (
            rider.type === RiderType.CIR &&
            (!Utils.exists(rider.amount) || !Utils.exists(rider.accelerationPercent))
          ) {
            return false;
          }
          return Utils.exists(rider.amount);
        }),
      asOfDate: Utils.now(),
    };

    premiumRequestList.requests.push(regularPremiumRequest);

    if (additionalRequests.includes('oppositePaymentFrequency')) {
      // if selected paymentFrequency = "Annual", this request is for the "Monthly" premium
      // if selected paymentFrequency = "Monthly", this request is for the "Annual" premium
      let oppositePaymentFrequencyVal = quote.paymentFrequency;
      if (CommonUtils.makePaymentFrequencySafeForCoverpathQuote(oppositePaymentFrequencyVal) === 'Annual') {
        oppositePaymentFrequencyVal = 'Monthly';
      } else {
        oppositePaymentFrequencyVal = 'Annual';
      }

      const oppositePaymentFrequencyRequest: PremiumRequest = Object.assign({}, regularPremiumRequest);
      oppositePaymentFrequencyRequest.paymentFrequency = oppositePaymentFrequencyVal;
      oppositePaymentFrequencyRequest.riders = null;
      premiumRequestList.requests.push(oppositePaymentFrequencyRequest);
    }

    let newProduct = quote.product;
    if (quote.product === Product.products.CASTerm.code || quote.product === Product.products.MMDirectTerm.code) {
      newProduct = quote.product + 'ECP';
    } else if (
      quote.product === Product.products.CASTermECP.code ||
      quote.product === Product.products.MMDirectTermECP.code
    ) {
      newProduct = quote.product.substring(0, quote.product.length - 3);
    }

    if (additionalRequests.includes('oppositeECPPremium')) {
      const oppositeECPPremiumRequest: PremiumRequest = Object.assign({}, regularPremiumRequest);
      oppositeECPPremiumRequest.product = newProduct;
      premiumRequestList.requests.push(oppositeECPPremiumRequest);
    }

    return premiumRequestList;
  }
}
